import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Loading, SearchSelectOptionProps } from "@alm/flow-ds/components";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import produce from "immer";
import { AxiosError } from "axios";
import { useSelector, useDispatch } from "react-redux";

import CustomLink from "components/CustomLink";
import InstancesCard from "./InstancesCard";
import usePagedData from "hooks/usePagedData";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import { ListGroup, ListGroupItem } from "../../../ListGroup";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import DeleteDialog from "components/DeleteDialog";
import TerminateDialog from "../../Modals/TerminateDialog";
import api from "lib/api";
import { useAlertsHandler } from "hooks/useAlertsHandler";
import { ActivityInstance } from "records/ActivityInstance";
import { getCheckedBoxes } from "data/app/appSelectors";
import {
	addCheckedBox,
	removeCheckedBox,
	setCheckedBoxes,
} from "data/app/appActions";
import ActivityInstancesHeading from "./ActivityInstancesHeading";
import { useMobileView } from "hooks/useMobileView";
import PaginationBar from "components/PaginationBar";
import {
	ActivityInstancesPayload,
	DateOperators,
} from "../../../../lib/api/services/workflow";
//import Table from "elements/LegacyArrayView/Table";
interface Props {
	search: string;
	status: string;
	createdBy: SearchSelectOptionProps | null;
	created: string;
	operator: DateOperators;
	setStatusValue: React.Dispatch<React.SetStateAction<string>>;
	setCreatedByValue: React.Dispatch<
		React.SetStateAction<SearchSelectOptionProps | null>
	>;
	setCreatedValue: React.Dispatch<React.SetStateAction<string>>;
	setOperatorValue: React.Dispatch<React.SetStateAction<DateOperators>>;
	setSearchValue: React.Dispatch<React.SetStateAction<string>>;
	listFilter: "ALL" | "ACTIVE" | "COMPLETED";
}

function Instances({
	search,
	listFilter,
	status,
	createdBy,
	created,
	operator,
	setStatusValue,
	setCreatedByValue,
	setCreatedValue,
	setOperatorValue,
	setSearchValue,
}: Props) {
	const { t } = useTranslation();

	const history = useHistory();
	const location = useLocation();

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	const [currentPage, setCurrentPage] = useState(1);

	const [total, setTotal] = useState<number | undefined>(0);

	const { paginationLimit } = useMobileView();

	const checkboxes = useSelector(getCheckedBoxes);
	const reduxDispatch = useDispatch();

	// Hook to page Activity instances
	const { state, setData, ready, loading, searching, patchState } =
		usePagedData();

	const { terminateId }: { terminateId?: string | string[] } =
		queryString.parse(window.location.search);

	const { deleteId }: { deleteId?: string | string[] } = queryString.parse(
		window.location.search
	);

	const { addErrorAlert } = useAlertsHandler();

	const { limit } = state;

	const disableOnLoad = state.loading ? "disabled" : "";

	const produceOffsetPatch = useCallback(
		(id: string | string[]) =>
			produce(state, draft => {
				if (Array.isArray(id)) {
					const indexes: number[] = [];
					for (const [index, instance] of draft.results.entries()) {
						id.includes(instance.id) && indexes.push(index);
					}
					for (let i = 0; i < indexes.length; i++) {
						draft.results.splice(indexes[i] - i, 1);
					}
					const newOffset = draft.offset - indexes.length;
					draft.offset = newOffset;
				} else {
					const index = draft.results.findIndex(
						(instance: ActivityInstance) => instance.id === id
					);
					draft.results.splice(index, 1);
					const newOffset = draft.offset - 1;
					draft.offset = newOffset;
				}
			}),
		[state]
	);

	const produceTerminate = useCallback(
		(id: string | string[]) =>
			produce(state, draft => {
				if (Array.isArray(id)) {
					const indexes: number[] = [];
					for (const [index, instance] of draft.results.entries()) {
						id.includes(instance.id) && indexes.push(index);
					}
					for (let i = 0; i < indexes.length; i++) {
						if (listFilter === "ACTIVE") {
							draft.results.splice(indexes[i] - i, 1);
						} else if (listFilter === "ALL") {
							draft.results[indexes[i]].status = "Completed";
						}
					}
					if (listFilter === "ACTIVE") {
						const newOffset = draft.offset - indexes.length;
						draft.offset = newOffset;
					}
				} else {
					const index = draft.results.findIndex(
						(instance: ActivityInstance) => instance.id === id
					);
					if (listFilter === "ACTIVE") {
						draft.results.splice(index, 1);
						const newOffset = draft.offset - 1;
						draft.offset = newOffset;
					} else if (listFilter === "ALL") {
						draft.results[index].status = "Completed";
					}
				}
			}),
		[listFilter, state]
	);

	/**
	 * Get menu and menu list items using id
	 *
	 * @id of instance
	 */
	const getMenuSelectors = useCallback(id => {
		return {
			menu: document.querySelector(
				`#activityInstanceMenu__dropdown__${id}`
			),
			menuList: document.querySelector(
				`#activityInstanceMenu__dropdown__${id} + div`
			),
			checkboxLabel: document.querySelector(
				`label[for="checkbox__${id}"]`
			),
			checkbox: document.querySelector(`#checkbox__${id}`),
		};
	}, []);

	/**
	 * Checks for clicks on the instance item to see if it's not the
	 * instance menu dropdown
	 *
	 * @param e event object
	 * @param id of instance
	 */
	const handleOnClick = useCallback(
		(id: string) => (e: React.MouseEvent) => {
			const target = e.target as HTMLElement;
			// Get menu selectors so we know which elements to ignore

			const { menu, menuList, checkboxLabel, checkbox } =
				getMenuSelectors(id);

			if (
				!checkboxLabel?.contains(target) &&
				!checkbox?.contains(target)
			) {
				e.preventDefault();
			}

			if (
				!menu?.contains(target) &&
				!menuList?.contains(target) &&
				!checkboxLabel?.contains(target) &&
				!checkbox?.contains(target)
			) {
				addSentryBreadcrumb({
					category: "click",
					message: `Activity instance ${id}`,
				});
				history.push({
					pathname: `/activity-instance/${id}`,
					state: {
						from: location.pathname + location.search,
					},
				});
			}
		},
		[getMenuSelectors, history, location.pathname, location.search]
	);

	/**
	 * Checks if key is spacebar or enter
	 *
	 * @param e event object
	 * @param id of instance
	 */
	const handleKeyDown = useCallback(
		(id: string) => (e: React.KeyboardEvent) => {
			const target = e.target as HTMLElement;
			// Get menu selectors so we know which elements to ignore
			const selectors = getMenuSelectors(id);

			// If key is spacebar or enter and target isn't instance dropdown then go to instance
			if (
				(e.key === "Enter" || e.keyCode === 32) &&
				!selectors.menu?.contains(target) &&
				!selectors.menuList?.contains(target) &&
				!selectors.checkbox?.contains(target)
			) {
				addSentryBreadcrumb({
					category: "keydown",
					message: `Activity instance ${id}`,
				});
				e.preventDefault();
				history.push({
					pathname: `/activity-instance/${id}`,
					state: {
						from: location.pathname + location.search,
					},
				});
			}
		},
		[getMenuSelectors, history, location.pathname, location.search]
	);

	useEffect(() => {
		setInit(false);
	}, [listFilter]);

	const getInstancesPayload = useCallback(() => {
		const params = new URLSearchParams(location.search);

		let newSearch: string | undefined = undefined;
		let newCreatedBy: string | undefined = undefined;
		let newCreated: string | undefined = undefined;
		let newOperator: DateOperators | undefined = undefined;
		let newStatus: "ACTIVE" | "ALL" | "COMPLETED" = "ALL";

		const paramSearch = params.get("search");
		const paramCreatedBy = params.get("createdBy");
		const paramCreated = params.get("created");
		const paramOperator = params.get("operator");
		const paramStatus = params.get("status");

		if (paramSearch !== null) {
			newSearch = paramSearch;
			setSearchValue(newSearch);
		}
		if (paramCreatedBy !== null) {
			newCreatedBy = paramCreatedBy;

			if (
				(createdBy !== null && createdBy.value !== newCreatedBy) ||
				createdBy === null
			)
				setCreatedByValue({ label: newCreatedBy, value: newCreatedBy });
		}
		if (paramCreated !== null) {
			newCreated = paramCreated;
			setCreatedValue(newCreated);
		}
		if (paramOperator !== null) {
			newOperator = paramOperator as DateOperators;
			setOperatorValue(newOperator);
		}
		if (paramStatus !== null) {
			newStatus =
				paramStatus === "active"
					? "ACTIVE"
					: paramStatus === "completed"
					? "COMPLETED"
					: "ALL";
			setStatusValue(newStatus.toLowerCase());
		}

		const payload: ActivityInstancesPayload = {
			offset: 0,
			limit: 10,
			search: newSearch,
			creator: newCreatedBy,
			date: newCreated,
			operator: newOperator !== "ALL" ? newOperator : undefined,
			status: newStatus === "ALL" ? undefined : newStatus,
		};
		return payload;
	}, [
		location.search,
		setSearchValue,
		createdBy,
		setCreatedByValue,
		setCreatedValue,
		setOperatorValue,
		setStatusValue,
	]);

	/**
	 * Load instances on mount
	 */
	useEffect(() => {
		if (!init) {
			setCurrentPage(1);
			// Load instances from API
			api.workflow
				.getActivityInstances(getInstancesPayload())
				.then(res => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(res);
						setTotal(res.totalCount);
						ready();
						setInit(true);
					}
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [init]);

	useEffect(() => {
		if (init) {
			searching();
			setCurrentPage(1);
			// Load activities from API with search query
			api.workflow
				.getActivityInstances(getInstancesPayload())
				.then(res => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(res);
						setTotal(res.totalCount);
						ready();
					}
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, createdBy, created, operator]);

	const pageData = useCallback(
		(page: number) => {
			addSentryBreadcrumb({
				category: "button.click",
				message: "Instances page",
			});

			loading();
			const payload: ActivityInstancesPayload = {
				limit,
				offset: (page - 1) * limit,
				search: state.search !== "" ? state.search : undefined,
				creator: createdBy !== null ? createdBy.value : undefined,
				status:
					status === "active"
						? "ACTIVE"
						: status === "completed"
						? "COMPLETED"
						: undefined,
				date: created !== "" ? created : undefined,
				operator: operator !== "ALL" ? operator : undefined,
			};
			api.workflow
				.getActivityInstances(payload)
				.then(res => {
					setData(res);
					ready();
				})
				.catch(e => console.error(e));
			setCurrentPage(page);
		},
		[
			loading,
			limit,
			state.search,
			createdBy,
			status,
			created,
			operator,
			setData,
			ready,
		]
	);

	const handleTerminate =
		(id: string | string[]) => async (reason: string) => {
			try {
				Array.isArray(id)
					? await api.workflow.terminateMultipleActivityInstances(
							id.filter(id =>
								state.results.find(
									instance =>
										instance.id === id &&
										instance.status === "Active"
								)
							),
							reason
					  )
					: await api.workflow.terminateActivityInstance(id, reason);
				patchState(produceTerminate(id));
			} catch (e) {
				const error = e as AxiosError;
				console.error(error);
				addErrorAlert(error, "errorTerminatingActivityInstance");
				history.push("/activity-instances");
			}
		};

	const handleDelete = (id: string | string[]) => async () => {
		try {
			Array.isArray(id)
				? await api.workflow.deleteMultipleActivityInstances(id)
				: await api.workflow.deleteActivityInstance(id);
			patchState(produceOffsetPatch(id));
		} catch (e) {
			const error = e as AxiosError;
			console.error(error);
			addErrorAlert(error, "errorTerminatingActivityInstance");
			history.push("/activity-instances");
		}
	};

	useEffect(() => {
		const newCheckboxes: string[] = [];
		for (const instance of state.results) {
			if (checkboxes.includes(instance.id))
				newCheckboxes.push(instance.id);
		}
		reduxDispatch(setCheckedBoxes(newCheckboxes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.results]);

	const selectedCount = useMemo(() => {
		return checkboxes.length;
	}, [checkboxes]);

	const selectedActiveCount = useMemo(() => {
		return checkboxes.filter(
			id =>
				!!state.results.find(
					instance =>
						instance.id === id && instance.status === "Active"
				)
		).length;
	}, [checkboxes, state.results]);

	const isActive = useMemo(() => {
		return checkboxes.some(uuid =>
			state.results.some(
				instance => instance.id === uuid && instance.status === "Active"
			)
		);
	}, [checkboxes, state.results]);

	const handleCheckboxClick = useCallback(
		(id: string) => () => {
			checkboxes.includes(id)
				? reduxDispatch(removeCheckedBox(id))
				: reduxDispatch(addCheckedBox(id));
		},
		[checkboxes, reduxDispatch]
	);

	const handleSelectAllCheckboxesClick = useCallback(() => {
		const newCheckboxes: string[] = [];
		if (!(checkboxes.length === state.results.length)) {
			for (const instance of state.results) {
				newCheckboxes.push(instance.id);
			}
		}
		reduxDispatch(setCheckedBoxes(newCheckboxes));
	}, [checkboxes.length, reduxDispatch, state.results]);

	const isSelectedRowClicked = useMemo(() => {
		if (terminateId === undefined) {
			return typeof deleteId === "string"
				? checkboxes.includes(deleteId)
				: deleteId?.every(id => checkboxes.includes(id));
		}
		return typeof terminateId === "string"
			? checkboxes.includes(terminateId)
			: terminateId.every(id => checkboxes.includes(id));
	}, [checkboxes, deleteId, terminateId]);

	const terminateTitle = t(
		selectedCount > 1 && isSelectedRowClicked
			? "main.activityInstances.headings.terminateActivities"
			: "main.activityInstances.headings.terminateActivity"
	);

	const terminateContentElement = (
		<div>
			<p>
				<span className="dialog-bold-text">
					{selectedActiveCount} {t("main.activityInstances.text.of")}{" "}
					{selectedCount}{" "}
				</span>
				{t(
					selectedCount > 1 && selectedActiveCount === selectedCount
						? "main.activityInstances.text.terminateAllSelected"
						: "main.activityInstances.text.terminateNotAllSelected"
				)}
			</p>
			<p></p>
			<p>{t("main.activityInstances.text.terminateActivities")}</p>
		</div>
	);

	const terminateContent =
		selectedCount > 1 && isSelectedRowClicked
			? terminateContentElement
			: t("main.activityInstances.text.terminateActivity");

	const deleteTitle = t(
		selectedCount > 1 && isSelectedRowClicked
			? "main.activityInstances.headings.deleteActivities"
			: "main.activityInstances.headings.deleteActivity"
	);
	const deleteContent =
		selectedCount > 1 && isSelectedRowClicked ? (
			<div>
				{t("main.activityInstances.text.deleteMultipleTextPart1")}{" "}
				<span className="dialog-bold-text">
					{selectedCount}{" "}
					{t("main.activityInstances.text.activityInstances")}
				</span>{" "}
				{t("main.activityInstances.text.deleteMultipleTextPart2")}
				<p> </p>
				{t("main.activityInstances.text.deleteActivities")}
			</div>
		) : (
			t("main.activityInstances.text.deleteActivity")
		);

	if (!init || state.searching)
		return <Loading className="mt-4 elem__loader--md" />;

	return (
		<>
			{state.results?.length ? (
				<>
					<div className="activity-instances-container--select">
						<div
							className="activity-instances--selectedAll"
							onClick={handleSelectAllCheckboxesClick}>
							{checkboxes.length === state.results.length
								? t("main.activityInstances.labels.deselectAll")
								: t("main.activityInstances.labels.selectAll")}
						</div>
					</div>
					<ActivityInstancesHeading
						isActive={isActive}
						handleSelectAllCheckboxesClick={
							handleSelectAllCheckboxesClick
						}
						instancesCount={state.results.length}
						loading={state.loading}
					/>
					<ListGroup className={`list-group-links ${disableOnLoad}`}>
						{state.results.map(instance => (
							<ListGroupItem
								className={`activity-instances-card--container ${
									checkboxes.includes(instance.id)
										? "activity-instances-card--selected"
										: ""
								} ${disableOnLoad}`}
								key={`activity-instance__${instance.id}`}>
								<CustomLink
									handleClick={handleOnClick(instance.id)}
									handleKeyDown={handleKeyDown(instance.id)}
									label={instance.id}
									to={`/activity-instance/${instance.id}`}>
									<InstancesCard
										{...instance}
										isActive={isActive}
										handleCheckboxClick={
											handleCheckboxClick
										}
										isChecked={checkboxes.includes(
											instance.id
										)}
										loading={state.loading}
									/>
								</CustomLink>
							</ListGroupItem>
						))}
					</ListGroup>
				</>
			) : (
				!state.loading && (
					<p className="no-instances">
						{t(
							search.trim()
								? "main.activityInstances.text.noSearch"
								: "main.activityInstances.text.noInstances"
						)}
					</p>
				)
			)}

			{total !== undefined && total > limit && !state.searching && (
				<PaginationBar
					currentPage={currentPage}
					limit={limit}
					total={total}
					paginationLimit={paginationLimit}
					onPageChange={page => {
						pageData(page);
					}}
					loading={state.loading}
				/>
			)}

			{terminateId && (
				<TerminateDialog
					isOpen
					terminateElement={handleTerminate(terminateId)}
					title={terminateTitle}
					content={terminateContent}
				/>
			)}
			{deleteId && (
				<DeleteDialog
					isOpen
					deleteElement={handleDelete(deleteId)}
					title={deleteTitle}
					content={deleteContent}
				/>
			)}
		</>
	);
}

export default Instances;
